import { Box, Button, Circle, Flex, Grid, GridItem, Text, useDisclosure, Icon } from "@chakra-ui/react";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import { useEffect, useState, useCallback } from "react";
import { fetchAnaliseVacancy, fetchCandidateList, resetCurrentOffers, resetCurrentOffersCount, setCurrentOffers, setCurrentOffersCount, setCurrentVacancyName, setCurrentVacancyStatus, setDefaultCount, setDefaultSources } from "../../store/slices/analytics";
import { ColoredBar } from "../ColoredBar/ColoredBar";
import { InfoStatusModal } from "./AnalyticsModals/InfoStatusModal";
import { SourceDetails } from "../../api/analytics/types";
import { useNavigate, useParams } from "react-router-dom";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import { exportAnalytics } from "../../api/analytics";
import { DownloadIcon, AttachmentIcon } from "@chakra-ui/icons";
import { AnalyticsFilters } from "./AnalyticsFilters";

export const SingleMainAnalyticBlock = () => {
    const dispatch = useAppDispatch();
    const [isExporting, setIsExporting] = useState(false);

    const { id } = useParams();

    const { isOpen: isStatusOpen, onOpen: onStatusOpen, onClose: onStatusClose } = useDisclosure();

    const [currentStatusSelect, setCurrentStatus] = useState<string | null>(null)
    const [pinnedStage, setPinnedStage] = useState<{
        id: number;
        name: string;
        sources: any;
        total: number;
    } | null>(null);
    const [hoveredStage, setHoveredStage] = useState<number | null>(null);

    const { currentVacancyName,currentStatus, analiseVacancy, customer, currentSources, totalSourceCount, defaultSources, defaultCount, period_from, period_to, status, recruiter, currentChannel } = useAppSelector(
        (state: RootState) => state.analytics
    );

    const aggregateSources = useCallback((data: any) => {
        const aggregatedSources:any = {};
        let totalCount = 0;

        data.forEach((item:any) => {
            if (item.sources) {
                for (const key in item.sources) {
                    if (item.sources.hasOwnProperty(key)) {
                        const source = item.sources[key];
                        const sourceName = source.name;

                        if (aggregatedSources[sourceName]) {
                            aggregatedSources[sourceName].count += source.count;
                        } else {
                            aggregatedSources[sourceName] = {
                                name: source.name,
                                color: source.color,
                                count: source.count,
                            };
                        }
                        totalCount += source.count;
                    }
                }
            }
        });
        dispatch(setDefaultCount(totalCount))
        return Object.values(aggregatedSources);
    }, [dispatch]);

    useEffect(() => {
        dispatch(setDefaultSources(aggregateSources(analiseVacancy)))
    }, [analiseVacancy, dispatch, aggregateSources])


    const handleMouseEnter = (sources: any, total: number, status_name: string, stageId: number) => {
        // Если этап закреплен, не обновляем информацию при наведении на другие этапы
        if (pinnedStage && pinnedStage.id !== stageId) {
            return;
        }
        
        setHoveredStage(stageId);
        const sourceList: SourceDetails[] = Object.values(sources).map(
            (sourceDetail: any) => ({
                name: sourceDetail.name,
                color: sourceDetail.color,
                count: sourceDetail.count,
            })
        );
        dispatch(setCurrentVacancyStatus(status_name))
        dispatch(setCurrentOffersCount(total))
        dispatch(setCurrentOffers(sourceList))
    };

    const handleMouseLeave = () => {
        // Если этап закреплен, не сбрасываем информацию при уходе курсора
        if (pinnedStage) {
            setHoveredStage(null);
            return;
        }
        
        setHoveredStage(null);
        dispatch(resetCurrentOffersCount())
        dispatch(resetCurrentOffers())
        dispatch(setCurrentVacancyStatus(null))
    }

    const handlePinStage = (stage: { id: number; name: string; sources: any; total: number }) => {
        setPinnedStage(stage);
        const sourceList: SourceDetails[] = Object.values(stage.sources).map((sourceDetail: any) => ({
            name: sourceDetail.name,
            color: sourceDetail.color,
            count: sourceDetail.count,
        }));
        dispatch(setCurrentVacancyStatus(stage.name));
        dispatch(setCurrentOffersCount(stage.total));
        dispatch(setCurrentOffers(sourceList));
    };

    const handleUnpinStage = () => {
        setPinnedStage(null);
        setHoveredStage(null);
        dispatch(resetCurrentOffersCount());
        dispatch(resetCurrentOffers());
        dispatch(setCurrentVacancyStatus(null));
    };

    useEffect(() => {
        if (id) {
            dispatch(fetchAnaliseVacancy({ period_from: period_from, period_to: period_to, vacancy_id: Number(id) }))
        }

    }, [period_from, period_to, id, dispatch])

    const navigate = useNavigate();

    useEffect(() => {
        if (!currentVacancyName) {
            navigate("/analytics")
        }
        return () => {
            dispatch(setCurrentVacancyName(null))
        }
    }, [dispatch, navigate, currentVacancyName])



    return (
        <Flex flexDirection="column" w="100%" overflow="none">
            <AnalyticsFilters
                isActive={true}
                noDefaultFilters={true}
                additionalFilters={
                    <DateRangePicker withCheckbox={true} />
                }
                exportButton={
                    <Button
                        onClick={async () => {
                            try {
                                setIsExporting(true);
                                const response = await exportAnalytics({
                                    period_from: period_from,
                                    period_to: period_to,
                                    vacancy_id: Number(id)
                                });
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', 'analytics.xlsx');
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(url);
                            } catch (error) {
                                console.error('Export error:', error);
                            } finally {
                                setIsExporting(false);
                            }
                        }}
                        leftIcon={<DownloadIcon />}
                        colorScheme="green"
                        variant="outline"
                        borderWidth="2px"
                        isLoading={isExporting}
                        loadingText="Экспорт..."
                        _hover={{
                            bg: "green.50",
                            transform: "translateY(-2px)",
                            boxShadow: "lg",
                        }}
                        _active={{
                            transform: "translateY(0)",
                        }}
                        transition="all 0.2s"
                    >
                        Экспорт в Excel
                    </Button>
                }
            />
            <Box bgColor="white" borderRadius="8px" border="1px solid #e1e7ef" padding="16px" marginTop="12px" minH="70vh">
                <Text fontSize="27px" fontWeight={500} color="#3b515e">
                    Вакансии → Воронка
                </Text>
                <Text fontSize="27px" fontWeight={500} color="#3b515e" marginBottom="16px">
                    {currentVacancyName}
                </Text>
                <Grid templateColumns="repeat(3, 1fr)" gap="30px">
                <GridItem colSpan={1}>
                    <Flex flexDirection="column" gap="12px" color="#65758b">
                        <Text marginBottom="6px">Этап</Text>
                        {analiseVacancy.map((el) => {
                            return (
                                <>
                                    {Number(el.total) !== 0 && (
                                        <Flex
                                            key={el.id}
                                            alignItems="center"
                                            justifyContent="space-between"
                                            padding="6px"
                                            borderRadius="6px"
                                            position="relative"
                                            onMouseEnter={() => handleMouseEnter(el.sources, el.total, el.name, el.id)}
                                            onMouseLeave={handleMouseLeave}
                                            bg={pinnedStage?.id === el.id ? "#e6f3ff" : "transparent"}
                                            border={pinnedStage?.id === el.id ? "2px solid #61a6fa" : "2px solid transparent"}
                                            _hover={{
                                                backgroundColor: pinnedStage?.id === el.id ? "#e6f3ff" : "#f0f0f0",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <Text
                                                onClick={() => {
                                                    setCurrentStatus(el.name)
                                                    dispatch(fetchCandidateList({ vacancy_application_status: el.id, period_from:period_from, period_to: period_to, recruiter_id: recruiter, customer_id: customer, vacancy_id: Number(id), vacancy_status: status, channel_id: currentChannel === 0 ? null : currentChannel }))
                                                    onStatusOpen()
                                                }}
                                                fontWeight={600}
                                                color={pinnedStage?.id === el.id ? "#61a6fa" : (hoveredStage === el.id ? "#61a6fa" : "#65758b")}
                                                flex="1"
                                            >
                                                {el.name}
                                            </Text>
                                            {(hoveredStage === el.id || pinnedStage?.id === el.id) && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    minW="auto"
                                                    h="auto"
                                                    p="4px"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (pinnedStage?.id === el.id) {
                                                            handleUnpinStage();
                                                        } else {
                                                            handlePinStage({
                                                                id: el.id,
                                                                name: el.name,
                                                                sources: el.sources,
                                                                total: el.total,
                                                            });
                                                        }
                                                    }}
                                                    _hover={{
                                                        bg: pinnedStage?.id === el.id ? "red.100" : "blue.100",
                                                    }}
                                                >
                                                    <Icon
                                                        as={AttachmentIcon}
                                                        w="14px"
                                                        h="14px"
                                                        color={pinnedStage?.id === el.id ? "red.500" : "blue.500"}
                                                        transform={pinnedStage?.id === el.id ? "rotate(45deg)" : "rotate(0deg)"}
                                                        transition="all 0.2s"
                                                    />
                                                </Button>
                                            )}
                                        </Flex>
                                    )}
                                </>
                            )
                        })}
                    </Flex>
                </GridItem>
                <GridItem colSpan={1}>
                    <Flex flexDirection="column" gap="12px" color="#65758b">
                        <Text marginBottom="6px">Кандидаты и источники</Text>
                        {analiseVacancy.map((el) => {
                            const sourceList: any[] = Object.entries(el.sources).map(
                                ([key, sourceDetail]: [string, any]) => ({
                                    name: sourceDetail.name,
                                    color: sourceDetail.color,
                                    count: sourceDetail.count,
                                    id: Number(key),
                                })
                            );
                            return (
                                <>
                                    {Number(el.total) !== 0 && (

                                        <Flex key={el.id} padding="8px">
                                            <Text w="40px" fontWeight={600}>{el.total}</Text>
                                            <ColoredBar data={sourceList} status_id={el.id} vacancy_status={el.name} isSinglePage={true} vacancy_id={Number(id)} />
                                        </Flex>
                                    )}
                                </>

                            )
                        })}
                    </Flex>
                </GridItem>
                <GridItem colSpan={1} w="20vw" border="1px solid #e1e7ef" borderRadius="16px" padding="16px" height="max-content" position="sticky" top="20px">
                    {currentSources.length > 0 || totalSourceCount !== null ? (
                        <Flex w="100%" flexDirection="column" gap="6px">
                            <Flex w="100%" justifyContent="space-between" bgColor="#f3f4f680" padding="12px" borderRadius="6px">
                                <Text color="#65758b" fontWeight={500}>{currentStatus ? currentStatus : "Всего кандидатов:"}</Text>
                                <Text color="#3b515e" fontWeight={700}>{totalSourceCount}</Text>
                            </Flex>
                            <Text color="#3b515e" fontWeight={600} marginBottom="6px">Кандидаты по источникам:</Text>
                            {currentSources.map((el) => {
                                return (
                                    <Flex key={el.name} w="100%" justifyContent="space-between" padding="6px 12px" borderRadius="6px" bgColor="white" border="1px solid #e1e7ef" _hover={{
                                        backgroundColor: "#f3f4f680",
                                        cursor: "pointer",
                                    }}>
                                        <Flex alignItems="center">
                                            <Circle size="0.75em" bg={el.color} mr="0.5em" />
                                            <Text>{el.name}</Text>
                                        </Flex>
                                        <Text>{el.count}</Text>
                                    </Flex>
                                )
                            })}
                            <Box borderTop="1px solid #e1e7ef" marginTop="12px" paddingTop="12px">
                                <Text color="#65758b" fontSize="12px" fontWeight={400} textAlign="center">
                                    Наведите на этап для детальной информации
                                </Text>
                            </Box>
                        </Flex>
                    ) : (
                        <Flex w="100%" flexDirection="column" gap="6px">
                            <Flex w="100%" justifyContent="space-between" bgColor="#f3f4f680" padding="12px" borderRadius="6px">
                                <Text color="#65758b" fontWeight={500}>Всего кандидатов:</Text>
                                <Text color="#3b515e" fontWeight={700}>{defaultCount}</Text>
                            </Flex>
                            <Text color="#3b515e" fontWeight={600} marginBottom="6px">Кандидаты по источникам:</Text>
                            {defaultSources.map((el) => {
                                return (
                                    <Flex key={el.name} w="100%" justifyContent="space-between" padding="6px 12px" borderRadius="6px" bgColor="white" border="1px solid #e1e7ef" _hover={{
                                        backgroundColor: "#f3f4f680",
                                        cursor: "pointer",
                                    }}>
                                        <Flex alignItems="center">
                                            <Circle size="0.75em" bg={el.color} mr="0.5em" />
                                            <Text>{el.name}</Text>
                                        </Flex>
                                        <Text>{el.count}</Text>
                                    </Flex>
                                )
                            })}
                            <Box borderTop="1px solid #e1e7ef" marginTop="12px" paddingTop="12px">
                                <Text color="#65758b" fontSize="12px" fontWeight={400} textAlign="center">
                                    Наведите на этап для детальной информации
                                </Text>
                            </Box>
                        </Flex>
                    )}
                </GridItem>
            </Grid>
            </Box>
            <InfoStatusModal currentStatus={currentStatusSelect} isOpen={isStatusOpen} onClose={onStatusClose} />
        </Flex>
    )
}